import { useCallback, useMemo, useRef } from 'react';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { getBoardIdFromRoute } from '@trello/business-logic-react/board';

import { SMART_SCHEDULE_AI_ANALYTICS_CONFIG } from './smartScheduleAiAnalyticsConfig';
import { type SmartScheduleOnboardingEntryPoint } from './smartScheduleOnboardingSharedState';
import { type SmartScheduleOnboardingAiRuleType } from './useUpsertSmartScheduleOnboardingAiRule';

/**
 * The `screen` analytics dimension for each onboarding step. Sent as an attribute
 * on in-modal events (rather than encoded in the source) so the funnel is one
 * source grouped by `screen`.
 */
export type SmartScheduleOnboardingScreen =
  | 'allSetScreen'
  | 'connectAccountScreen'
  | 'rulesScreen'
  | 'startScreen'
  | 'workingHoursScreen';

const SOURCE = 'smartScheduleOnboardingModal';

const SCREEN_EVENT_NAMES = {
  allSetScreen: 'smartScheduleOnboardingAllSetScreen',
  connectAccountScreen: 'smartScheduleOnboardingConnectAccountScreen',
  rulesScreen: 'smartScheduleOnboardingRulesScreen',
  startScreen: 'smartScheduleOnboardingStartScreen',
  workingHoursScreen: 'smartScheduleOnboardingWorkingHoursScreen',
} as const satisfies Record<SmartScheduleOnboardingScreen, string>;

// The differing parts of an in-modal UI event. `source`, `containers`, and the
// shared AI config are constant and added by the wrapper. Picking from the real
// `sendUIEvent` signature keeps `action` / `actionSubject` / `actionSubjectId`
// glossary-type-checked.
type OnboardingUIEvent = Pick<
  Parameters<typeof Analytics.sendUIEvent>[0],
  'action' | 'actionSubject' | 'actionSubjectId' | 'attributes'
>;

interface CompletedAttributes {
  hasBoardSelected: boolean;
  isProactiveEnabled: boolean;
  workingHoursCustomized: boolean;
  hasSecondaryRule: boolean;
  secondaryRuleType: SmartScheduleOnboardingAiRuleType | null;
}

interface SavedAttributes {
  boardSaved: boolean;
  proactiveSaved: boolean;
  workingHoursRuleSaved: boolean;
  secondaryRuleSaved: boolean;
}

/**
 * Centralizes every Smart Schedule onboarding analytics event so the dialog only
 * supplies the parts that differ (the `actionSubjectId`, `screen`, and any
 * event-specific attributes). All events share one `source`, the route-derived
 * board `containers`, and the AI feature config.
 *
 * @param entryPoint The surface that opened onboarding, attached to the
 * modal-viewed, start-screen, and completion events. `null` until plumbed.
 */
export const useSmartScheduleOnboardingAnalytics = (
  entryPoint: SmartScheduleOnboardingEntryPoint | null,
) => {
  // The current board is the only board we can attribute events to without
  // sending UGC; it's route-derived and stable for the dialog's lifetime.
  const boardId = getBoardIdFromRoute();
  const containers = useMemo(() => formatContainers({ boardId }), [boardId]);

  const trackUIEvent = useCallback(
    ({ attributes, ...event }: OnboardingUIEvent) => {
      Analytics.sendUIEvent({
        ...event,
        source: SOURCE,
        containers,
        attributes: { ...SMART_SCHEDULE_AI_ANALYTICS_CONFIG, ...attributes },
      });
    },
    [containers],
  );

  const trackScreenViewed = useCallback(
    (screen: SmartScheduleOnboardingScreen) => {
      Analytics.sendScreenEvent({
        name: SCREEN_EVENT_NAMES[screen],
        containers,
        attributes: {
          ...SMART_SCHEDULE_AI_ANALYTICS_CONFIG,
          // Only the entry screen carries the entry point.
          ...(screen === 'startScreen' ? { entryPoint } : {}),
        },
      });
    },
    [containers, entryPoint],
  );

  // The "saw onboarding" denominator. Idempotent so it fires once per open even
  // across re-renders; the hook (and dialog) remount on each open, resetting it.
  const hasSentViewedEvent = useRef(false);
  const trackModalViewed = useCallback(() => {
    if (hasSentViewedEvent.current) {
      return;
    }
    hasSentViewedEvent.current = true;
    trackUIEvent({
      action: 'viewed',
      actionSubject: 'modal',
      actionSubjectId: 'smartScheduleOnboardingModal',
      attributes: { entryPoint },
    });
  }, [trackUIEvent, entryPoint]);

  const trackCompleted = useCallback(
    (attributes: CompletedAttributes) => {
      Analytics.sendTrackEvent({
        action: 'completed',
        actionSubject: 'onboarding',
        actionSubjectId: 'smartSchedule',
        source: SOURCE,
        containers,
        attributes: {
          ...SMART_SCHEDULE_AI_ANALYTICS_CONFIG,
          entryPoint,
          ...attributes,
        },
      });
    },
    [containers, entryPoint],
  );

  const trackSaved = useCallback(
    (attributes: SavedAttributes) => {
      Analytics.sendTrackEvent({
        action: 'saved',
        actionSubject: 'smartSchedulePreferences',
        actionSubjectId: 'onboarding',
        source: SOURCE,
        containers,
        attributes: { ...SMART_SCHEDULE_AI_ANALYTICS_CONFIG, ...attributes },
      });
    },
    [containers],
  );

  return {
    trackUIEvent,
    trackScreenViewed,
    trackModalViewed,
    trackCompleted,
    trackSaved,
  };
};
